import React, { useEffect, useState, useRef, memo } from "react";
import { motion, animate, useInView } from "framer-motion";

const AnimatedCircularProgress = ({
  progress = 75,
  strokeWidth = 10,
  duration = 1.5,
  label = "Innovation Metric",
  circleColor = "#ea3d95",
  trackColor = "#282829",
  // New prop
  forceAnimation = false,
}) => {
  // Attach a ref to the container and watch for in-view status.
  const containerRef = useRef(null);
  // Keep once: false with margin to prevent edge case flickers
  const isInView = useInView(containerRef, { once: false, margin: "-10% 0px" });

  // Define SVG constants using a 100x100 viewBox.
  const center = 50;
  const radius = 45; // Leaves room for stroke within the viewBox.
  const circumference = 2 * Math.PI * radius;

  // Ensure progress is always a valid number
  const safeProgress =
    typeof progress === "number" && isFinite(progress) && !isNaN(progress)
      ? progress
      : 75;

  const targetOffset = circumference - (safeProgress / 100) * circumference;

  // Local state to update the displayed percentage.
  const [displayedPercentage, setDisplayedPercentage] = useState(0);
  // A key that increments each time the component enters view, forcing a re-mount of animated elements.
  const [animationKey, setAnimationKey] = useState(0);
  // State to track if component is ready for animation
  const [isReady, setIsReady] = useState(false);

  // Prevent flickering edge cases with this flag
  const animatingRef = useRef(false);

  // Make sure all DOM elements are ready before starting animations
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Respond to both useInView and forceAnimation prop
  useEffect(() => {
    // Trigger animation when either in view naturally OR forced by parent
    if ((isInView || forceAnimation) && isReady) {
      // If already animating, don't restart
      if (animatingRef.current) return;

      // Mark as animating
      animatingRef.current = true;

      // Reset percentage and update key to force re-run of the animation.
      setDisplayedPercentage(0);
      setAnimationKey((prev) => prev + 1);

      try {
        const controls = animate(0, safeProgress, {
          duration: duration,
          onUpdate(value) {
            setDisplayedPercentage(Math.round(value));
          },
          onComplete() {
            // Reset flag when animation completes
            animatingRef.current = false;
          },
        });

        return () => {
          controls.stop();
          // Also reset flag if component unmounts mid-animation
          animatingRef.current = false;
        };
      } catch (err) {
        console.error("Animation error:", err);
        // Fallback in case of animation error
        setDisplayedPercentage(safeProgress);
        animatingRef.current = false;
      }
    } else if (!isInView && !forceAnimation) {
      // Reset animation flag when out of view and not forced
      animatingRef.current = false;
    }
  }, [isInView, forceAnimation, safeProgress, duration, isReady]);

  if (!isReady) {
    return (
      <div
        ref={containerRef}
        className="flex flex-col items-center"
        style={{ minHeight: "200px" }}
      ></div>
    );
  }

  return (
    <div ref={containerRef} className="flex flex-col items-center">
      <motion.div
        className="relative"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <svg className="w-full h-auto" viewBox="0 0 100 100">
          {/* Background track circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            stroke={trackColor}
            fill="transparent"
            strokeWidth={strokeWidth}
          />
          {/* Animated progress circle (rotated so it starts at the top) */}
          <motion.circle
            key={animationKey}
            cx={center}
            cy={center}
            r={radius}
            stroke={circleColor}
            fill="transparent"
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={circumference}
            transform="rotate(-90 50 50)"
            animate={{ strokeDashoffset: targetOffset }}
            transition={{ duration: duration, ease: "easeOut" }}
            strokeLinecap="round"
          />
        </svg>
        {/* Animated label and percentage text inside the pie */}
        <motion.div
          className="absolute inset-0 flex flex-col items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: duration }}
        >
          <motion.span
            className="font-semibold text-[#ffffff]"
            style={{ fontSize: "clamp(1.5rem, 5vw, 2rem)" }}
          >
            {label}
          </motion.span>
          <motion.span
            className="font-bold text-[#ffffff]"
            style={{
              fontSize: "clamp(2.1rem, 7vw, 4.2rem)",
              marginTop: "-0.5rem",
              marginBottom: "-0.5rem",
            }}
          >
            {displayedPercentage}%
          </motion.span>
        </motion.div>
      </motion.div>
    </div>
  );
};

// Use memo to prevent unnecessary rerenders from parent
export default memo(AnimatedCircularProgress);
